import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, FileText, Lock, Sparkles, Trophy, Users, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/lib/I18nContext";
import { useDateTimeFormat } from "@/lib/TzContext";
import { pickText } from "@/lib/bilingual";

const TABS = [
  { id: "home", labelKey: "special.tabs.home" },
  { id: "prize", labelKey: "special.tabs.prize" },
  { id: "result", labelKey: "special.tabs.result" },
];

const pdfHref = (url) => {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  return url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${url}` : url;
};

export default function PollasEspeciales() {
  const { user, updateNickname } = useAuth();
  const { t, lang } = useI18n();
  const fmt = useDateTimeFormat();
  const navigate = useNavigate();

  const [pools, setPools] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("home");
  const [editingNick, setEditingNick] = useState(false);
  const [nickDraft, setNickDraft] = useState(user?.nickname || "");

  const loadPools = async () => {
    try {
      const r = await api.get("/special-pools");
      const arr = r.data || [];
      setPools(arr);
      if (!arr.length) {
        setSelectedId("");
        setDetail(null);
      } else if (!selectedId || !arr.find((p) => p.special_pool_id === selectedId)) {
        setSelectedId(arr[0].special_pool_id);
      }
    } catch {
      // Silently ignore — the empty list will fall back to the empty-state UI.
    }
  };

  const loadDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await api.get(`/special-pools/${id}`);
      setDetail(r.data);
    } catch (e) {
      setDetail(null);
      toast.error(e?.response?.data?.detail || t("special.errors.load"));
    }
  };

  useEffect(() => { loadPools(); }, []);  // eslint-disable-line
  useEffect(() => { loadDetail(selectedId); }, [selectedId]);

  const fmtPoolLabel = (p) => {
    const t = pickText(p.title, lang);
    if (p.home_team_name && p.away_team_name) return `${p.home_team_name} vs ${p.away_team_name}`;
    return t || "Sin configurar";
  };

  useEffect(() => { setNickDraft(user?.nickname || ""); }, [user]);

  const saveNick = async () => {
    try {
      const trimmed = nickDraft.trim();
      if (!trimmed || trimmed === user?.nickname) { setEditingNick(false); return; }
      await updateNickname(trimmed);
      setEditingNick(false);
      toast.success(t("special.nickSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    }
  };

  if (!pools.length) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Sparkles size={48} className="mx-auto mb-4 text-[var(--c-yellow)]" />
          <h1 className="font-display text-3xl uppercase mb-2">{t("special.empty.title")}</h1>
          <p className="text-zinc-400">{t("special.empty.body")}</p>
          {!user?.is_special_only && (
            <button onClick={() => navigate("/perfil")} data-testid="special-empty-back"
              className="mt-6 bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm">
              {t("common.back")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-[var(--c-yellow)] font-mono-data">
            {t("special.title")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase">
            {detail ? (pickText(detail.title, lang) || t("special.title")) : t("special.title")}
          </h1>
        </div>
        {pools.length > 1 && (
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="special-pool-selector"
            className="bg-zinc-900 border border-zinc-800 focus:border-[var(--c-yellow)] px-3 py-2 text-sm rounded-sm font-mono-data uppercase tracking-wider"
          >
            {pools.map((p) => (
              <option key={p.special_pool_id} value={p.special_pool_id}>
                {fmtPoolLabel(p)}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800 flex gap-0 overflow-x-auto" role="tablist">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            role="tab"
            data-testid={`special-tab-${tb.id}`}
            className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap transition-colors ${
              tab === tb.id ? "text-white border-b-2 border-[var(--c-yellow)]" : "text-zinc-400 hover:text-white"
            }`}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {!detail ? (
        <div className="text-zinc-400">{t("common.loading")}</div>
      ) : (
        <>
          {tab === "home" && (
            <HomeTab
              detail={detail}
              user={user}
              nickDraft={nickDraft}
              setNickDraft={setNickDraft}
              editingNick={editingNick}
              setEditingNick={setEditingNick}
              saveNick={saveNick}
              onSavedPrediction={() => loadDetail(selectedId)}
              t={t}
              lang={lang}
              fmt={fmt}
            />
          )}
          {tab === "prize" && <PrizeTab detail={detail} t={t} lang={lang} />}
          {tab === "result" && <ResultTab detail={detail} t={t} lang={lang} />}
        </>
      )}
    </div>
  );
}

function HomeTab({ detail, user, nickDraft, setNickDraft, editingNick, setEditingNick, saveNick, onSavedPrediction, t, lang, fmt }) {
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [saving, setSaving] = useState(false);
  const locked = detail.is_locked;

  useEffect(() => {
    setHome(detail.my_prediction?.home_score ?? "");
    setAway(detail.my_prediction?.away_score ?? "");
  }, [detail.special_pool_id, detail.my_prediction?.updated_at]);

  const save = async () => {
    const h = parseInt(home, 10);
    const a = parseInt(away, 10);
    if (Number.isNaN(h) || Number.isNaN(a) || h < 0 || a < 0) {
      toast.error(t("special.errors.scoreInvalid"));
      return;
    }
    setSaving(true);
    try {
      await api.put(`/special-pools/${detail.special_pool_id}/prediction`, {
        home_score: h, away_score: a,
      });
      toast.success(t("special.predictionSaved"));
      onSavedPrediction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err.save"));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Hello + edit nickname */}
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("special.welcomeLabel")}</div>
        {editingNick ? (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={nickDraft}
              onChange={(e) => setNickDraft(e.target.value)}
              maxLength={20}
              data-testid="special-nick-input"
              className="flex-1 min-w-[180px] bg-black border border-zinc-800 focus:border-[var(--c-yellow)] px-3 py-2 outline-none rounded-sm"
            />
            <button onClick={saveNick} data-testid="special-nick-save"
              className="bg-[var(--c-yellow)] text-black font-bold uppercase tracking-wider px-3 py-2 text-xs">
              {t("common.save")}
            </button>
            <button onClick={() => setEditingNick(false)} className="border border-zinc-800 text-zinc-300 px-3 py-2 text-xs uppercase tracking-wider">
              {t("common.cancel")}
            </button>
          </div>
        ) : (
          <>
            <div className="font-display text-4xl sm:text-5xl uppercase">
              {t("profile.hello")} <span className="text-[var(--c-lime)]" translate="no">{user?.nickname}</span>
            </div>
            <button onClick={() => setEditingNick(true)} data-testid="special-nick-edit"
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-zinc-800 hover:border-white text-zinc-300 px-3 py-1.5">
              <Edit2 size={12} /> {t("profile.editNick")}
            </button>
          </>
        )}
      </div>

      {/* Match card */}
      <div className="bg-zinc-900 border border-zinc-800 overflow-hidden" data-testid="special-match-card">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-2 text-[11px] font-mono-data uppercase tracking-widest text-zinc-400">
          <div className="flex items-center gap-2">
            <Calendar size={12} />
            <span>{detail.kickoff ? fmt.shortYear(detail.kickoff) : "—"}</span>
            {detail.kickoff && <span>· {fmt.time(detail.kickoff)}</span>}
          </div>
          {detail.venue && (
            <div className="flex items-center gap-1 truncate">
              <MapPin size={12} /> <span className="truncate">{detail.venue}</span>
            </div>
          )}
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-3 items-center gap-3">
            <TeamSide name={detail.home_team_name} img={detail.home_team_image_url} side="home" />
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("special.yourPick")}</div>
              <div className="flex items-center justify-center gap-2">
                <input
                  type="number" min="0" max="50"
                  value={home}
                  onChange={(e) => setHome(e.target.value)}
                  disabled={locked}
                  data-testid="special-score-home"
                  className="w-14 sm:w-16 h-14 sm:h-16 text-center font-display text-3xl sm:text-4xl bg-black border border-zinc-800 focus:border-[var(--c-yellow)] outline-none disabled:opacity-50 rounded-sm"
                />
                <span className="text-zinc-500 text-2xl">-</span>
                <input
                  type="number" min="0" max="50"
                  value={away}
                  onChange={(e) => setAway(e.target.value)}
                  disabled={locked}
                  data-testid="special-score-away"
                  className="w-14 sm:w-16 h-14 sm:h-16 text-center font-display text-3xl sm:text-4xl bg-black border border-zinc-800 focus:border-[var(--c-yellow)] outline-none disabled:opacity-50 rounded-sm"
                />
              </div>
            </div>
            <TeamSide name={detail.away_team_name} img={detail.away_team_image_url} side="away" />
          </div>
          <div className="mt-5 flex items-center justify-center gap-3">
            {locked ? (
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[var(--c-peach)] font-mono-data">
                <Lock size={14} /> {t("special.locked")}
              </div>
            ) : (
              <button onClick={save} disabled={saving} data-testid="special-save-prediction"
                className="bg-[var(--c-lime)] text-black font-bold uppercase tracking-wider px-6 py-3 text-sm disabled:opacity-50">
                {saving ? t("common.saving") : (detail.my_prediction ? t("special.updatePrediction") : t("special.savePrediction"))}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Rules PDF */}
      {detail.rules_pdf_url && (
        <a href={pdfHref(detail.rules_pdf_url)} target="_blank" rel="noopener noreferrer"
          data-testid="special-rules-pdf"
          className="bg-zinc-900 border border-zinc-800 hover:border-[var(--c-lime)] p-4 flex items-center gap-3">
          <FileText className="text-[var(--c-lime)]" size={20} />
          <div className="flex-1">
            <div className="font-display text-lg uppercase">{t("special.rulesPdf.title")}</div>
            <div className="text-xs text-zinc-400">{t("special.rulesPdf.subtitle")}</div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[var(--c-lime)] font-mono-data">{t("rules.pdf.download")} ↗</span>
        </a>
      )}
    </div>
  );
}

function TeamSide({ name, img, side }) {
  return (
    <div className="flex flex-col items-center gap-2 text-center" data-testid={`special-team-${side}`}>
      {img ? (
        <img src={img} alt={name} className="w-16 h-16 sm:w-24 sm:h-24 object-contain" translate="no" />
      ) : (
        <div className="w-16 h-16 sm:w-24 sm:h-24 bg-zinc-800 flex items-center justify-center text-3xl">⚽</div>
      )}
      <div className="font-display text-sm sm:text-lg uppercase break-words" translate="no">{name}</div>
    </div>
  );
}

function PrizeTab({ detail, t, lang }) {
  const prize = detail.prize;
  const predictions = detail.predictions;
  const locked = detail.is_locked;
  return (
    <div className="space-y-6">
      {prize ? (
        <div className="border border-zinc-800 px-6 py-6 text-center" data-testid="special-prize-box">
          <div className="font-display text-xl md:text-2xl uppercase text-white tracking-wide">{t("ranking.prize.title")}</div>
          <div className="font-display text-3xl md:text-5xl text-white leading-none mt-2 break-words">{prize.amount_text}</div>
          <div className="text-xs text-zinc-400 mt-3 max-w-md mx-auto">{t("special.prize.splitHint")}</div>
        </div>
      ) : (
        <div className="text-zinc-500 text-sm italic" data-testid="special-prize-empty">{t("special.prize.empty")}</div>
      )}

      {!locked ? (
        <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
          <Lock size={32} className="mx-auto mb-3 text-zinc-500" />
          <div className="font-display text-xl uppercase">{t("special.preds.lockedTitle")}</div>
          <div className="text-zinc-400 text-sm mt-1">{t("special.preds.lockedSubtitle")}</div>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800" data-testid="special-predictions-list">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="font-display text-xl uppercase">{t("special.preds.title")}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
              {predictions?.length || 0} {t("special.preds.entries")}
            </div>
          </div>
          {(predictions || []).map((p, i) => (
            <div key={p.user_id} data-testid={`special-pred-${i}`}
              className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-zinc-900 text-sm">
              <div className="col-span-7 truncate" translate="no">{p.nickname}</div>
              <div className="col-span-5 text-right font-mono-data font-bold text-[var(--c-lime)]">
                {p.home_score} - {p.away_score}
              </div>
            </div>
          ))}
          {(!predictions || predictions.length === 0) && (
            <div className="px-4 py-6 text-center text-zinc-500 text-sm">{t("special.preds.empty")}</div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultTab({ detail, t, lang }) {
  const published = !!detail.result_published && detail.result_home != null && detail.result_away != null;
  const winners = detail.winners || [];
  return (
    <div className="space-y-6">
      {!published ? (
        <div className="bg-zinc-900 border border-zinc-800 p-6 text-center" data-testid="special-result-empty">
          <Trophy size={32} className="mx-auto mb-3 text-zinc-500" />
          <div className="font-display text-xl uppercase">{t("special.result.pendingTitle")}</div>
          <div className="text-zinc-400 text-sm mt-1">{t("special.result.pendingSubtitle")}</div>
        </div>
      ) : (
        <>
          <div className="bg-zinc-900 border border-zinc-800 p-6 text-center" data-testid="special-result-card">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-3">{t("special.result.official")}</div>
            <div className="grid grid-cols-3 items-center gap-3">
              <TeamSide name={detail.home_team_name} img={detail.home_team_image_url} side="home" />
              <div className="font-display text-5xl md:text-7xl text-[var(--c-yellow)]" data-testid="special-result-score">
                {detail.result_home} - {detail.result_away}
              </div>
              <TeamSide name={detail.away_team_name} img={detail.away_team_image_url} side="away" />
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800" data-testid="special-winners-list">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
              <div className="font-display text-xl uppercase flex items-center gap-2">
                <Trophy size={18} className="text-[var(--c-yellow)]" /> {t("special.result.winnersTitle")}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
                {winners.length} {t("special.result.winnersCount")}
              </div>
            </div>
            {winners.length === 0 ? (
              <div className="px-4 py-8 text-center text-zinc-400 text-sm">{t("special.result.noWinners")}</div>
            ) : (
              winners.map((w, i) => (
                <div key={w.user_id} data-testid={`special-winner-${i}`}
                  className="px-4 py-3 border-b border-zinc-900 text-sm flex items-center gap-3">
                  <Users size={14} className="text-[var(--c-lime)]" />
                  <span className="flex-1 truncate" translate="no">{w.nickname}</span>
                  <span className="font-mono-data text-[var(--c-yellow)] font-bold">{w.home_score} - {w.away_score}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
